import { useQueryClient } from '@tanstack/react-query'
import {
  usePutServicetypesServiceTypeId,
  getGetServicetypesQueryKey,
  getGetServicetypesServiceTypeIdQueryKey,
} from '@/api/generated/service-type/service-type'
import type { ServiceTypeAdminApiInContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'
import { getViewServiceErrorMessage } from './getViewServiceErrorMessage'
import { getServiceApiErrorBody } from '@/hooks/getServiceApiErrorBody'

/**
 * Saves (PUT) a service type. Replaces the `saveService` thunk + SAVE_SERVICE* flow.
 *
 * Old side effects, reproduced:
 * - SAVE_SERVICE -> loading: exposed via `isPending`.
 * - SAVE_SERVICE_SUCCESS -> `saveSuccess` flag (the page watched it to leave edit mode):
 *   surfaced through the caller's `onSuccess` callback.
 * - SAVE_SERVICE_ERROR -> mapped message: surfaced through `onError(message)`.
 * Invalidates the shared service-types collection and the per-id query so the management list
 * (useServiceTypes) and this page's `useService` refetch the updated data.
 */
export const useSaveService = () => {
  const queryClient = useQueryClient()
  const { mutate, isPending } = usePutServicetypesServiceTypeId()

  const saveService = (
    body: ServiceTypeAdminApiInContract,
    id: string,
    opts?: { onSuccess?: () => void; onError?: (message: string) => void },
  ) => {
    mutate(
      { serviceTypeId: id, data: body },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: getGetServicetypesQueryKey() })
          void queryClient.invalidateQueries({
            queryKey: getGetServicetypesServiceTypeIdQueryKey(id),
          })
          opts?.onSuccess?.()
        },
        onError: (error) => {
          opts?.onError?.(getViewServiceErrorMessage(getServiceApiErrorBody(error)))
        },
      },
    )
  }

  return { saveService, isPending }
}
